<script lang="ts">
import { defineComponent, PropType, ref, computed, watch } from 'vue'
import { extractPrefecture } from '@/utilities/companySearchQuery'

interface PrefectureChip {
  label: string,
  value: string,
}

/** 都道府県チップを出す最小候補数（少なければ一覧を眺めた方が早い） */
const PREFECTURE_FILTER_MIN_RESULTS = 3
const PREFECTURE_FILTER_MIN_PREFECTURES = 2

export default defineComponent({
  name: 'CompanySearchResultList',

  props: {
    /**
     * 会社名検索の候補一覧
     */
    results: {
      type: Array as PropType<CompanySearchResults>,
      required: true,
    },
  },

  emits: ['select', 'click:guideLink'],

  setup(props) {
    // 画面内に複数のコンポーネント表示があり得るので、instance ごとに一意の ID を生成して誤挙動を回避する
    const instanceId = ref(Math.random().toString(36).substring(2, 15))
    const selectedPrefecture = ref('')

    const prefectures = computed<string[]>(() => {
      const found = props.results.map(result => extractPrefecture(result.address)).filter(prefecture => !!prefecture)

      return [...new Set(found)]
    })

    const isShowPrefectureFilter = computed<boolean>(() => {
      return props.results.length >= PREFECTURE_FILTER_MIN_RESULTS && prefectures.value.length >= PREFECTURE_FILTER_MIN_PREFECTURES
    })

    const prefectureChips = computed<PrefectureChip[]>(() => {
      return [{ label: 'すべて', value: '' }, ...prefectures.value.map(prefecture => ({ label: prefecture, value: prefecture }))]
    })

    const filteredResults = computed<CompanySearchResults>(() => {
      if (!selectedPrefecture.value) return props.results

      return props.results.filter(result => extractPrefecture(result.address) === selectedPrefecture.value)
    })

    // 候補が入れ替わったら絞り込みは意味を失うのでリセットする
    watch(() => props.results, () => {
      selectedPrefecture.value = ''
    })

    return {
      // data
      instanceId,
      selectedPrefecture,

      // computed
      isShowPrefectureFilter,
      prefectureChips,
      filteredResults,
    }
  }
})
</script>

<template>
  <div class="o-list__companySearchResult">
    <dl class="o-list__companySearchResult--unit">
      <!-- dd だけの dl にしないため dt は常に出す。heading スロットが無いサジェストでは件数のみになる -->
      <dt class="o-list__companySearchResult--term">
        <slot name="heading" />
        <span
          class="span u-text__xx-small__fixed u-text__before u-text__after"
          data-before="["
          data-after="]">
          検索結果：{{ filteredResults.length }}件
        </span>
      </dt>

      <dd
        v-if="isShowPrefectureFilter"
        class="o-list__companySearchResult--description is-prefectureFilter">
        <div class="o-list__prefectureFilter">
          <dl class="o-list__prefectureFilter--unit">
            <dt
              :id="`prefecture-filter-label-${instanceId}`"
              class="o-list__prefectureFilter--term">
              都道府県で絞り込む
            </dt>

            <dd class="o-list__prefectureFilter--description">
              <div
                class="o-list is-horizontal"
                role="radiogroup"
                :aria-labelledby="`prefecture-filter-label-${instanceId}`">
                <!-- radiogroup が radio を直接所有するよう、間に挟まる list / listitem のロールを打ち消す -->
                <ul
                  class="o-list--items"
                  role="none">
                  <li
                    v-for="chip in prefectureChips"
                    :key="chip.value"
                    class="o-list--item"
                    role="none">
                    <div
                      class="o-selector__prefectureChip"
                      :data-active="chip.value === selectedPrefecture">
                      <label class="o-selector__prefectureChip--label">
                        <input
                          v-model="selectedPrefecture"
                          class="o-selector__prefectureChip--item"
                          type="radio"
                          :name="`prefecture-${instanceId}`"
                          :value="chip.value">
                        {{ chip.label }}
                      </label>
                    <!-- /.o-selector__prefectureChip --></div>
                  </li>
                </ul>
              <!-- /.o-list --></div>
            </dd>
          </dl>
        <!-- /.o-list__prefectureFilter --></div>
      </dd>

      <dd class="o-list__companySearchResult--description is-companySuggest">
        <!--
          @mousedown.prevent はクリック時にフォーカスリングが出るのを抑えるためのもの。
          浮動サジェストの開閉には影響しない（InputCompanyName の onFocusOut は relatedTarget が
          null のとき＝フォーカスの移動先が分からないときは閉じず、backdrop の判定に委ねるため）。
          Safari はボタンをクリックしてもフォーカスを与えないので、ここで挙動を揃える意味もある
        -->
        <div class="o-list__companySuggest">
          <ul class="o-list__companySuggest--items">
            <li
              v-for="result in filteredResults"
              :key="result.corporate_number"
              class="o-list__companySuggest--item">
              <div class="o-action__companySuggest">
                <button
                  class="o-action__companySuggest--item"
                  @mousedown.prevent
                  @click.prevent="$emit('select', result)">
                  <span class="o-action__companySuggest--label is-name">
                    {{ result.company_name }}
                  </span>
                  <span class="o-action__companySuggest--label is-address">
                    {{ result.address }}
                  </span>
                </button>
              <!-- /.o-action__companySuggest --></div>
            </li>
          </ul>
        <!-- /.o-list__companySuggest --></div>

        <div class="o-note u-text__xx-small u-text__bold">
          <p class="o-note--item">
            お勤め先が見当たらない場合は
            <button
              class="button u-text__bold"
              @mousedown.prevent
              @click.prevent="$emit('click:guideLink')">
              別の探し方
            </button>
            をお試しください
          </p>
        <!-- /.o-note --></div>
      </dd>
    </dl>
  <!-- /.o-list__companySearchResult --></div>
</template>

<style lang="sass" scoped>
$container-vertical-gap: 0.75em
$container-horizontal-gap: 1em

.o-list__companySearchResult
  // prefecture が表示されない場合は、会社サジェストが先頭にくるため、その場合はサジェストボタンの padding で隙間が確保されるため、上部 padding のみ、term で管理する
  & &--unit
    padding: 0 $container-horizontal-gap $container-vertical-gap

  & &--term
    color: $font-gray
    font-size: $font-x-small
    font-weight: bold

  & &--description
    &.is-prefectureFilter
      margin: $container-vertical-gap 0

      &:first-child
        margin-top: 0

  & &--term,
  & &--description.is-prefectureFilter:first-child
    padding-top: $container-vertical-gap

.o-list__prefectureFilter
  & &--term
    color: $font-gray
    font-size: $font-xx-small

  & &--description
    margin-top: 0.4em

    &:first-child
      margin-top: 0

.o-list__companySuggest
  margin: 1em (-$container-horizontal-gap) 0
  border-top: 1px solid $border-x-light-gray
  border-bottom: 1px solid $border-x-light-gray

  &:first-child
    margin-top: 0
    border-top: none

  &:last-child
    margin-bottom: (-$container-vertical-gap)
    border-bottom: none

  & &--item
    border-top: 1px solid $border-x-light-gray

    &:first-child
      border-top: none

.o-selector__prefectureChip
  $root: &
  // 視覚的に隠した input の包含ブロックをチップ自身に固定しておく
  // （現状は top / left が auto でフロー上の位置に描かれるため見た目は変わらないが、
  //  将来 top / left を指定したときに遠い祖先が基準にならないようにする）
  position: relative

  // reset で appearance: none が当たっており、ネイティブ描画を復元する normalize は要素クラス .input 前提で効かない。
  // 選択状態は label 側の data-active で表現するため、input は視覚的に隠したうえでフォーカスは受け取れるままにする
  & &--item
    width: 1px
    height: 1px
    position: absolute
    opacity: 0

  & &--label
    width: 100%
    min-height: 1em
    display: block
    font-size: $font-xx-small
    padding: 0.5em 1em
    background-color: $bg-white
    border: 1px solid $border-xx-light-cyan
    border-radius: $border-radius
    +transition

    +mouseover
      background-color: rgba($bg-x-light-cyan, 1)

    // input を隠しているため、キーボード操作時のフォーカス位置は label 側で示す
    &:focus-within
      outline: 2px solid $border-light-cyan
      outline-offset: 1px

  &[data-active="true"]
    #{$root}--label
      font-weight: bold
      background-color: rgba($bg-x-light-cyan, 1)

.o-action__companySuggest
  $button-height-px: 60
  & &--item
    width: 100%
    min-height: changeSizePx2Em($button-height-px)
    display: block
    text-align: left
    padding: 0.5em $container-horizontal-gap
    +transition

    +mouseover
      background-color: rgba($bg-x-light-cyan, 1)

  & &--label
    display: block

    &.is-name
      font-size: $font-x-small

    &.is-address
      color: $font-gray
      font-size: $font-xx-small
      margin-top: 0.4em

      &:first-child
        margin-top: 0
</style>
